import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import { navigateSerial } from "@/composable/useRoute";
import { useParams } from "react-router-dom";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import { serialsNavigationMain } from "@/constants/serials";

import SerieCard from "@/components/series/SerieCard";
import AppHeader from "../components/header/AppHeader";
import Navigation from "@/components/navigation/Navigation";
import Order from "@/pages/Order";
import MassAddition from "@/pages/MassAddition";
import SerieForm from "@/components/series/SerieForm";
import BlockSerialInfo from "@/components/blocks/BlockSerialInfo";
import BlockWhiteText from "@components/blocks/BlockWhiteText";
import VoiceoverReorder from "@/components/voiceover/VoiceoverReorder";

import api from "@/api";

import "../styles/serial-series.scss";
import {getResponseErrorMessage} from "@/utils/api-service";

function SerialSeries() {
  const [series, setSeries] = useState([]);
  const [activeSeason, setActiveSeason] = useState<string | number>(null);
  const [view, setView] = useState("list-series");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mainInfo, setMainInfo] = useState(null);
  const [seasonVoiceovers, setSeasonVoiceovers] = useState<any[]>([]);

  const { id } = useParams();

  const navigate = useNavigate();

  const linkBack = `/serial/${id}`;

  const onRemoveSerie = async (index: number, data:any) => {
    try {
      setSeries((prevSeasons) => prevSeasons.filter((_, i) => i !== index));

      await api.serie.removeEpisode(id, activeSeason, data.id);
      fetchSeries()

      notification.success({
          message: 'Сериал успешно удалён'
      });

    } catch(err) {
      console.error(err)
      const messageFormatter = getResponseErrorMessage(err, 'Ошибка при удалении сериала')
      notification.error({
          message: messageFormatter
      });
    }
  };

  const updateView = (key:string) => {
    setView(key)
  }

  const updateSubNav = (key: string) => {
    setView(key);
  };

  const onUpdateNavBottom = (val:any) => {
    console.log('val', val)
  }

  const onUpdateNav = (key: string) => {
    let url = ''

    if(key === 'massAddition') {
      setView(key)
      return

    } else {
      url = navigateSerial(key, id);
    }

    navigate(url);
  };

  const handleChangeSeason = (season_id) => {
    setActiveSeason(season_id);
  };

  const fetchSeries = async () => {
    try {
      if (id && activeSeason) {
        setIsLoading(true);

        const response = await api.season.getEpisodes(id, activeSeason);
        setSeries(response?.data?.data?.episodes || [])
        return;
      }

      setSeries([]);

    } catch (err) {
      console.error(err);
      setSeries([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSeasonVoiceoverOrder = async () => {
    if (!id || !activeSeason) return;
    try {
      const response = await api.season.getVoiceoversOrder(id, activeSeason);
      setSeasonVoiceovers(response.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSereal = async() => {
    try {
      const response = await api.serie.get(id)
      setMainInfo(response.data.data)

    } catch(err) {
      setMainInfo(null)
    }
  }

  const activeSeasonId = useMemo(() => {
    return activeSeason ? activeSeason : null
  }, [activeSeason])

  useEffect(() => {
    if (view === "list-series") {
      fetchSeries();
    }
  }, [activeSeason, view]);

  useEffect(() => {
    fetchSeasonVoiceoverOrder();
  }, [activeSeason]);

  useEffect(() => {
    fetchSereal()
  }, [])

  return (
    <div className="serial-series app-page">
      <AppHeader title="Редактирование сериала" link={linkBack} />

      {isLoading ? (
        <div className="app-loading-block">
          <Spin className="app-loading-block__spin" size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <div className="app-container serial-series__container">
          { mainInfo && <BlockWhiteText text={mainInfo.title || ''} /> }

          <Navigation
            defaultActiveLink="serial-series"
            links={serialsNavigationMain}
            update={onUpdateNav}
          />

          <BlockSerialInfo activeNavItem={view} serialId={id} update={handleChangeSeason} defaultValue={activeSeason} updateNav={updateSubNav} />

          {view === "create-serie" && (
            <SerieForm serialId={id} seasonId={activeSeason} movie={mainInfo} showList={() => updateSubNav('list-series')} />
          )}

          {view === "list-series" && (
            <div className="serial-series__series">
              {series && (
                <div className="serial-season__sections">
                  {series.map((serie, index) => {
                    return (
                      <SerieCard
                        key={index}
                        index={index + 1}
                        data={serie}
                        isEdit={true}
                        serial_id={id}
                        season_id={activeSeason}

                        remove={onRemoveSerie}
                        update={fetchSeries}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {view === "voiceover-order" && (
            <div className="serial-series__series">
              {series && series.length > 0 ? (
                <div className="serial-season__sections">
                  {series.map((serie, index) => {
                    return (
                      <SerieCard
                        key={index}
                        index={index + 1}
                        data={serie}
                        isEdit={false}
                        serial_id={id}
                        season_id={activeSeason}

                        remove={onRemoveSerie}
                        update={fetchSeries}
                      />
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '2rem' }}>
                  Нет эпизодов для настройки порядка озвучек
                </p>
              )}
            </div>
          )}

          {view === "order" && <Order season={activeSeason} showList={() => updateSubNav('list-series')} />}

          { view === 'massAddition' && <MassAddition serial_id={id} season_id={activeSeasonId} updateNav={updateView} /> }

          <VoiceoverReorder
            mode="season"
            voiceovers={seasonVoiceovers.map((v: any) => ({
              video_id: 0,
              content_source_id: v.content_source_id,
              name: v.name || '',
              sort_order: v.sort_order ?? null,
            }))}
            visible={view === 'voiceover-order' && !!activeSeason}
            serialId={id}
            seasonId={activeSeason}
            onClose={() => updateSubNav('list-series')}
            onSaved={() => { fetchSeries(); fetchSeasonVoiceoverOrder(); }}
          />
        </div>
      )}
    </div>
  );
}

export default SerialSeries;
